/**
 * A/B Testing Utilities
 * 
 * Helper functions for A/B testing variants, conversions, and feature flags.
 * Works with the ABTestFramework component.
 */

import { useOptionalABTest } from '@/components/shared/ABTestFramework';

/**
 * Get the variant for a specific test
 * @param testId - The ID of the A/B test
 * @returns The variant name (e.g., 'control', 'variant-a') or null if not in test
 */
export function getTestVariant(testId: string): string | null {
  try {
    const savedVariants = localStorage.getItem('ab-test-variants');
    if (savedVariants) {
      const variants = JSON.parse(savedVariants);
      return variants[testId] || null;
    }
  } catch (error) {
    console.warn('Failed to get test variant:', error);
  }
  return null;
}

/**
 * Track a conversion for an A/B test
 * @param testId - The ID of the A/B test
 * @param conversionType - The type of conversion (e.g., 'click', 'purchase', 'signup')
 * @param value - Optional numeric value (e.g., purchase amount)
 */
export function trackConversion(testId: string, conversionType: string, value?: number): void {
  // Track in analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'ab_test_conversion', {
      test_id: testId,
      conversion_type: conversionType,
      value: value,
      variant: getTestVariant(testId)
    });
  }

  // Store conversion in localStorage for analysis
  try {
    const conversionsKey = `ab-test-conversions-${testId}`;
    const existing = localStorage.getItem(conversionsKey);
    const conversions = existing ? JSON.parse(existing) : [];
    
    conversions.push({
      conversionType,
      value,
      timestamp: new Date().toISOString(),
      variant: getTestVariant(testId)
    });
    
    localStorage.setItem(conversionsKey, JSON.stringify(conversions));
  } catch (error) {
    console.warn('Failed to store conversion:', error);
  }
}

/**
 * Check if a feature should be shown based on A/B test
 * @param featureId - The feature ID to check
 * @param testId - Optional test ID (if different from featureId)
 * @returns true if feature should be shown, false otherwise
 */
export function shouldShowFeature(featureId: string, testId?: string): boolean {
  const variant = getTestVariant(testId || featureId);
  
  // If no variant assigned, default to false (don't show experimental features)
  if (!variant) {
    return false;
  }
  
  // For boolean features, 'enabled' or 'true' variants show the feature
  return variant === 'enabled' || variant === 'true' || variant === 'variant-a';
}

/**
 * Get the configuration for a specific test
 * @param testId - The ID of the A/B test
 * @returns The test configuration object or null
 */
export function getTestConfig(testId: string): unknown | null {
  // This would ideally come from a backend or config file
  // For now, we'll use the predefined tests from ABTestFramework
  const testConfigs: Record<string, unknown> = {
    'ad-density-test': {
      control: { adDensity: 'normal', adsPerPage: 3 },
      'high-density': { adDensity: 'high', adsPerPage: 5 }
    },
    'affiliate-cta-test': {
      control: { ctaPosition: 'bottom', ctaStyle: 'button' },
      prominent: { ctaPosition: 'top', ctaStyle: 'banner' }
    },
    'rpp-default-test': {
      control: { rppDefault: false },
      'rpp-enabled': { rppDefault: true }
    },
    'pro-upgrade-test': {
      control: { promptAfter: 3, promptType: 'modal' },
      'early-prompt': { promptAfter: 1, promptType: 'banner' }
    }
  };

  const variant = getTestVariant(testId);
  if (!variant || !testConfigs[testId]) {
    return null;
  }

  return testConfigs[testId][variant] || null;
}

/**
 * Hook version of getTestVariant for React components
 * Uses the ABTestFramework context
 */
export function useTestVariant(testId: string): string | null {
  const context = useOptionalABTest();
  if (context) {
    return context.getVariant(testId);
  }
  return getTestVariant(testId);
}

/**
 * Hook version of trackConversion for React components
 * Uses the ABTestFramework context
 */
export function useTrackConversion() {
  const context = useOptionalABTest();
  if (context) {
    return context.trackConversion;
  }
  return trackConversion;
}

/**
 * Check if user is in a specific test
 * @param testId - The ID of the A/B test
 * @returns true if user is assigned to this test
 */
export function isInTest(testId: string): boolean {
  return getTestVariant(testId) !== null;
}

/**
 * Get all active tests for the current user
 * @returns Array of test IDs the user is participating in
 */
export function getActiveTests(): string[] {
  try {
    const savedVariants = localStorage.getItem('ab-test-variants');
    if (savedVariants) {
      const variants = JSON.parse(savedVariants);
      return Object.keys(variants);
    }
  } catch (error) {
    console.warn('Failed to get active tests:', error);
  }
  return [];
}

/**
 * Get conversion rate for a test (client-side calculation)
 * @param testId - The ID of the A/B test
 * @returns Object with conversion counts and rates per variant
 */
export function getTestStats(testId: string): {
  variant: string;
  conversions: number;
  impressions: number;
  conversionRate: number;
}[] {
  const stats: Record<string, { conversions: number; impressions: number }> = {};
  
  try {
    // Get conversions
    const conversionsKey = `ab-test-conversions-${testId}`;
    const conversionsData = localStorage.getItem(conversionsKey);
    if (conversionsData) {
      const conversions = JSON.parse(conversionsData);
      conversions.forEach((conv: unknown) => {
        const variant = conv.variant || 'unknown';
        if (!stats[variant]) {
          stats[variant] = { conversions: 0, impressions: 0 };
        }
        stats[variant].conversions++;
      });
    }

    // Get impressions (would need to be tracked separately)
    // For now, we'll estimate based on variant assignments
    const variant = getTestVariant(testId);
    if (variant) {
      if (!stats[variant]) {
        stats[variant] = { conversions: 0, impressions: 0 };
      }
      stats[variant].impressions = 1; // Placeholder
    }
  } catch (error) {
    console.warn('Failed to get test stats:', error);
  }

  return Object.entries(stats).map(([variant, data]) => ({
    variant,
    conversions: data.conversions,
    impressions: data.impressions,
    conversionRate: data.impressions > 0 ? (data.conversions / data.impressions) * 100 : 0
  }));
}

